import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { supabase } from '../supabaseClient';

interface AvatarUploadProps {
  currentAvatarUrl: string | null;
  onAvatarUpdate: (newAvatarUrl: string | null) => void;
  userId: string;
  size?: number;
  userName?: string;
  userEmail?: string;
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatarUrl,
  onAvatarUpdate,
  userId,
  size = 120,
  userName,
  userEmail,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB');
      return;
    }

    setSelectedFile(file);
    setError('');

    // Create preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Check user authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated. Please log in again.');
      }

      console.log('Starting upload for user:', user.id);
      console.log('Selected file:', selectedFile.name, 'Size:', selectedFile.size);

      // Generate unique filename
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      console.log('Upload path:', fileName);

      // Delete old avatar if exists
      if (currentAvatarUrl) {
        const oldPath = currentAvatarUrl.split('/').slice(-2).join('/'); // Get userId/filename
        console.log('Removing old avatar:', oldPath);

        const { error: deleteError } = await supabase.storage
          .from('profile-images')
          .remove([oldPath]);

        if (deleteError) {
          console.warn('Could not delete old avatar:', deleteError);
          // Don't throw error, just log it
        }
      }

      // Upload new avatar
      console.log('Uploading to storage...');
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('profile-images')
        .upload(fileName, selectedFile, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      console.log('Upload successful:', uploadData);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(fileName);

      console.log('Generated public URL:', publicUrl);

      // Update profile in database
      console.log('Updating profile in database...');
      const { error: dbError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (dbError) {
        console.error('Database update error:', dbError);
        throw new Error(`Database update failed: ${dbError.message}`);
      }

      console.log('Profile updated successfully');

      // Update parent component
      onAvatarUpdate(publicUrl);
      setDialogOpen(false);
      setPreviewUrl(null);
      setSelectedFile(null);

    } catch (err: any) {
      console.error('Upload error:', err);

      // Provide more specific error messages
      let errorMessage = 'Failed to upload image';
      if (err.message?.includes('storage')) {
        errorMessage = 'Storage error: Please check your permissions and try again';
      } else if (err.message?.includes('permission')) {
        errorMessage = 'Permission denied: Please ensure you are logged in';
      } else if (err.message?.includes('size')) {
        errorMessage = 'File too large: Please choose an image smaller than 5MB';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!currentAvatarUrl) return;

    setUploading(true);
    try {
      // Check user authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated. Please log in again.');
      }

      // Delete from storage
      const oldPath = currentAvatarUrl.split('/').slice(-2).join('/'); // Get userId/filename
      console.log('Removing avatar from storage:', oldPath);

      const { error: deleteError } = await supabase.storage
        .from('profile-images')
        .remove([oldPath]);

      if (deleteError) {
        console.error('Storage delete error:', deleteError);
        // Continue anyway to clear the URL from database
      }

      // Update profile in database
      const { error: dbError } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user.id);

      if (dbError) {
        throw new Error(`Database update failed: ${dbError.message}`);
      }

      // Update parent component
      onAvatarUpdate(null);
      setDialogOpen(false);

    } catch (err: any) {
      console.error('Remove error:', err);
      setError(err.message || 'Failed to remove avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleDialogClose = () => {
    if (uploading) return; // Prevent closing during upload

    setDialogOpen(false);
    setPreviewUrl(null);
    setSelectedFile(null);
    setError('');

    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  const getAvatarSrc = () => {
    if (previewUrl) return previewUrl;
    if (currentAvatarUrl) return currentAvatarUrl;
    return undefined;
  };

  const getAvatarInitial = () => {
    if (userName) {
      const nameParts = userName.split(' ');
      if (nameParts.length >= 2) {
        return nameParts[0].charAt(0) + nameParts[1].charAt(0);
      } else if (nameParts[0]) {
        return nameParts[0].charAt(0);
      }
    }
    if (userEmail) {
      return userEmail.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <>
      <Box sx={{ textAlign: 'center' }}>
        <Avatar
          src={currentAvatarUrl || undefined}
          sx={{
            width: size,
            height: size,
            mx: 'auto',
            mb: 2,
            fontSize: size / 6,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
          onClick={() => setDialogOpen(true)}
        >
          {getAvatarInitial()}
        </Avatar>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PhotoCameraIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Change Avatar
        </Button>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Update Profile Picture
            <IconButton onClick={handleDialogClose} disabled={uploading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {/* Current/Preview Avatar */}
            <Avatar
              src={getAvatarSrc()}
              sx={{
                width: 150,
                height: 150,
                mx: 'auto',
                mb: 3,
                fontSize: '3rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              {getAvatarInitial()}
            </Avatar>

            {/* File Input */}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              ref={fileInputRef}
              aria-label="Choose avatar image file"
            />

            {/* Upload Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                Choose Image
              </Button>

              {currentAvatarUrl && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleRemoveAvatar}
                  disabled={uploading}
                >
                  Remove
                </Button>
              )}
            </Box>

            {/* Upload Progress */}
            {uploading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
                  value={uploadProgress}
                />
                <Typography variant="caption" color="text.secondary">
                  {uploadProgress > 0 ? `${uploadProgress}%` : 'Uploading...'}
                </Typography>
              </Box>
            )}

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Guidelines */}
            <Typography variant="caption" color="text.secondary">
              Recommended: Square image, at least 200x200 pixels. Max size: 5MB.
              <br />
              Supported formats: JPG, PNG, GIF, WebP
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AvatarUpload; 